import React, {ReactNode, useCallback, useMemo} from 'react';
import {Autocomplete, Grid, Theme} from '@mui/material';
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import {createFilterOptions} from "@mui/material/useAutocomplete";
import {useTranslation} from "react-i18next";
import TextFieldWithValidation from "./input/TextFieldWithValidation";

const currencies: Currency[] = [
    {code: 'EUR', onTop: true},
    {code: 'USD', onTop: true},
    {code: 'GBP', onTop: true},
    {code: 'PLN'},
    {code: 'SEK'},
    {code: 'NOK'},
    {code: 'CZK'},
    {code: 'ISL'},
    {code: 'CAD'},
    {code: 'CHF'},
    {code: 'AUD'},
    {code: 'VND'},
    {code: 'INR'},
    {code: 'CNY'},
    {code: 'HUF'},
    {code: 'RON'},
    {code: 'PHP'},
    // {code: 'UAH'},
    // {code: 'RUB'},
];

interface Currency {
    code: string;
    onTop?: boolean;
}

const getOptionLabel = (option: Currency) => `${option.code}`;

const componentsProps = {
    paper: {
        sx: {
            width: "15rem",
        }
    },
};

const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            formControl: {
                width: "3.4rem",
            },
        })
);

interface CurrencySelectorProps {
    label: ReactNode;
    onChange: (currency: string) => void;
    value: string | undefined;
    fullWidth?: boolean;
    disabled?: boolean;
    hasError?: any | boolean | null | undefined,
    errorText?: ReactNode,
}

const CurrencySelector: React.FC<CurrencySelectorProps> = ({label, onChange, value, fullWidth, disabled, hasError, errorText}) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const translatedName = useCallback((code: string) => t(`currency.${code}`), [t])


    const renderInput = useCallback(
            (params: any) => {
                return <TextFieldWithValidation
                        {...params}
                        label={label}
                        sx={{"& .MuiInputBase-input": {textOverflow: "unset", minWidth: "3rem !important"}}}
                        hasError={hasError}
                        errorText={errorText}
                />;
            },
            [label, hasError, errorText]
    );

    const handleSelectChange = useCallback((event: React.ChangeEvent<{}>, currency?: Currency | null) => {
        if (currency) {
            onChange(currency.code);
        }
    }, [onChange]);


    const filterOptions = useMemo(() => createFilterOptions({
        stringify: (option: Currency) => `${option.code} ${translatedName(option.code)}`,
    }), [translatedName]);

    const renderOption = useCallback((props: React.HTMLAttributes<HTMLLIElement>, option: Currency) => <li {...props}>
        <Grid container spacing={1} justifyContent="space-between">
            <Grid item>{option.code}</Grid>
            <Grid item>{translatedName(option.code)}</Grid>
        </Grid>
    </li>, [translatedName]);

    //Null here removes uncontrolled warning in case disableClearable is true
    // @ts-ignore
    const valueObj: Currency = useMemo(() => currencies.find(it => it.code === value) ?? null, [value]);

    return (
            <Autocomplete
                    fullWidth={fullWidth}
                    disabled={disabled}
                    filterOptions={filterOptions}
                    className={classes.formControl}
                    value={valueObj}
                    onChange={handleSelectChange}
                    options={currencies}
                    groupBy={o => o.onTop ? t("currency.commonUsedGroup") : t("currency.otherGroup")}
                    getOptionLabel={getOptionLabel}
                    renderOption={renderOption}
                    renderInput={renderInput}
                    disableClearable
                    componentsProps={componentsProps}
                    isOptionEqualToValue={(o1, o2) => o1.code === o2.code}
            />
    );
};

export default CurrencySelector;